import React, { useState, useEffect } from "react";
import cx from "classnames";
import styles from "../scss/AppFormField.module.scss";
import { IAnswerProps } from "../interfaces/IAppFormData";
import { bankAccountValidate } from "../shared/AppFormHelper";
import PatternFormat from "react-number-format";
import bankValidator from "nz-bank-account-validator/lib/NZ-Bank-Account-Validator";
import ValidationMessage from "../../common/components/Forms/ValidationMessage/ValidationMessage";

interface IAFBankAccountInputProps {
    question: any;
    answerChanges: (answerParam: IAnswerProps) => void;
    textRef?: React.Ref<HTMLInputElement>;
}

interface IBankInput {
    floatValue: number;
    formattedValue: string;
    value: string;
}

const AFBankAccountInput: React.FC<IAFBankAccountInputProps> = (props) => {
    const { question, answerChanges } = props;

    const [value, setValue] = useState("");
    const [error, setError] = useState("");

    const errorMessage = "Please provide a valid NZ bank account number";

    const handleDefaultValue = ({
        accountBank,
        accountBranch,
        accountNumber,
        accountSuffix
    }) => {
        return `${accountBank}${accountBranch}${accountNumber}${accountSuffix}`;
    };

    const handleChanges = (account: IBankInput) => {
        let bankAccount = {};
        if (!bankValidator.validate(account.formattedValue)) {
            setError(errorMessage);
            answerChanges({
                questionId: question.id,
                answer: "",
                disclosure: []
            });
            return;
        }
        if (account.value) {
            const accountSuffix = String(account.value).substring(
                13,
                String(account.value).length
            );
            bankAccount = {
                accountBank: String(account.value).substring(0, 2),
                accountBranch: String(account.value).substring(2, 6),
                accountNumber: String(account.value).substring(6, 13),
                accountSuffix: String(accountSuffix).padStart(3, "0")
            };
        }
        setError("");
        answerChanges({
            questionId: question.id,
            answer: account.value ? JSON.stringify(bankAccount) : "",
            disclosure: []
        });
    };

    const handleAccountValue = (account: IBankInput) => {
        setValue(account.value);
        setError("");
        if (!bankAccountValidate(account.value)) {
            setError(errorMessage);
            answerChanges({
                questionId: question.id,
                answer: "",
                disclosure: []
            });
            return;
        }
        handleChanges(account);
    };

    useEffect(() => {
        if (question?.answer) {
            const answers = JSON.parse(question.answer);
            setValue(handleDefaultValue(answers));
            answerChanges({
                questionId: question.id,
                answer: JSON.stringify(answers),
                disclosure: []
            });
        }
    }, [question.id]);

    return (
        <div className={styles["af-bank-account-wrapper"]} id={question.id}>
            <div className={cx(styles["af-bank-account"])}>
                <label
                    className={styles["af-bank-account--label"]}
                    htmlFor="accountNumber">
                    Account Number
                </label>
                <PatternFormat
                    className={cx(
                        styles["af-bank-account--input"],
                        "flex-gap-s",
                        Boolean(error) ? styles["error"] : ""
                    )}
                    onValueChange={handleAccountValue}
                    value={value}
                    format="##-####-#######-###"
                    placeholder="00-0000-0000000-000"
                    getInputRef={props.textRef}
                    mask="  "
                />
                {Boolean(error) && <ValidationMessage>{error}</ValidationMessage>}
            </div>
        </div>
    );
};

export default AFBankAccountInput;
